/**
 * SubsidyReportPayload - the detail of a Sozialdepartement item (#2003): the
 * case (kind, due date, mutation diff, exit), the copy-ready sheet the office
 * pastes into the city's Kollab portal (one copy button per value and "Copy
 * all"), the readiness of "Mark filed" and the decision.
 *
 * The sheet comes from GET /subsidy-reports/:id/sheet in the server's order.
 * The generic Cancel action stays in the detail sheet's action bar; filing and
 * the decision carry data, so they have their own dialogs here.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, ClipboardCopy, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryErrorState } from '@/components/ui/query-error-state';
import { formatDateDisplay } from '@/lib/format';
import { formatMoneyCents } from '@/lib/money';
import type { subsidy_SubsidyReportStatementRowResponse } from '@/api';
import { useSubsidyReportSheet } from '@/hooks/useSubsidyReports';
import {
  SUBSIDY_REPORT_DIFF_LABELS,
  buildSubsidyReportCopyAll,
  subsidyReportKindLabel,
  subsidyReportSheetLabel,
  type SubsidyReportSheetField,
} from '@/types/subsidyReport';
import { SubsidyReportDecisionDialog, SubsidyReportFileDialog } from './SubsidyReportDialogs';

type I18nLike = { _: (d: { id: string; message?: string; values?: Record<string, unknown> }) => string };

interface SubsidyReportPayloadProps {
  itemId?: string;
  currentState?: string;
  i18n: I18nLike;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Opens the statement on the subsidy matching screen of its office (#2005). */
function statementLink(row: subsidy_SubsidyReportStatementRowResponse): string {
  const params = new URLSearchParams({ tab: 'inbound', inbound: 'subsidy', subsidy_office: row.office_id });
  if (row.payment_id) params.set('payment', row.payment_id);
  return `/billing/reconciliation?${params.toString()}`;
}

function statementStateLabel(i18n: I18nLike, state: string): string {
  switch (state) {
    case 'missing':
      return i18n._({ id: 'subsidyReport.statement.state.missing', message: 'Missing from the statement' });
    case 'raise':
      return i18n._({ id: 'subsidyReport.statement.state.raise', message: 'City figure above ours' });
    case 'release':
      return i18n._({ id: 'subsidyReport.statement.state.release', message: 'City figure below ours' });
    default:
      return state;
  }
}

async function copyText(text: string, i18n: I18nLike) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(i18n._({ id: 'subsidyReport.sheet.copied', message: 'Copied to the clipboard' }));
  } catch {
    toast.error(i18n._({ id: 'subsidyReport.sheet.copyFailed', message: 'Could not copy to the clipboard' }));
  }
}

function SheetRow({ field, label, i18n }: { field: SubsidyReportSheetField; label: string; i18n: I18nLike }) {
  const missing = field.missing;
  return (
    <div
      className="flex items-start justify-between gap-2 border-b py-2 last:border-b-0"
      data-testid={`subsidy-report-field-${field.key}`}
    >
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        {field.value ? (
          <div className="break-words text-sm">{field.value}</div>
        ) : missing ? (
          <div className="text-sm text-warning-on-surface" data-testid={`subsidy-report-missing-${field.key}`}>
            {field.key === 'pid'
              ? i18n._({ id: 'subsidyReport.sheet.pidUnknown', message: 'PID unknown' })
              : i18n._({ id: 'subsidyReport.sheet.missing', message: 'Missing' })}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">—</div>
        )}
        {missing && field.value ? (
          <div className="text-xs text-warning-on-surface">
            {i18n._({ id: 'subsidyReport.sheet.unmapped', message: 'No care type for these services' })}
          </div>
        ) : null}
      </div>
      {field.value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={i18n._({ id: 'subsidyReport.sheet.copyField', message: 'Copy {label}', values: { label } })}
          onClick={() => void copyText(field.value, i18n)}
          data-testid={`subsidy-report-copy-${field.key}`}
        >
          <Copy className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}

export function SubsidyReportPayload({ itemId, currentState, i18n }: SubsidyReportPayloadProps) {
  const query = useSubsidyReportSheet(itemId);
  const [fileOpen, setFileOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);

  if (query.isError) {
    return <QueryErrorState compact error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!query.data) {
    return <Skeleton className="h-64 w-full" data-testid="subsidy-report-skeleton" />;
  }

  const { item, fields, readiness } = query.data;
  const payload = item.payload;
  const state = currentState ?? item.current_state;
  const toFile = state === 'to_file';
  const filed = state === 'filed';
  const labelOf = (field: SubsidyReportSheetField) => subsidyReportSheetLabel(i18n, field);

  // A KmbB request (#2007) is filed against the two KmbB papers instead.
  const missingDocs: string[] = [];
  if (payload.kind === 'kmbb') {
    if (!readiness.kmbb_fachstelle_valid) {
      missingDocs.push(i18n._({ id: 'subsidyReport.readiness.kmbbFachstelle', message: 'KmbB specialist confirmation' }));
    }
    if (!readiness.kmbb_standortgespraech_valid) {
      missingDocs.push(i18n._({ id: 'subsidyReport.readiness.kmbbStandortgespraech', message: 'KmbB review meeting record' }));
    }
  } else {
    if (!readiness.bf_valid) {
      missingDocs.push(i18n._({ id: 'subsidyReport.readiness.bf', message: 'BF confirmation' }));
    }
    if (!readiness.sbu_valid) {
      missingDocs.push(i18n._({ id: 'subsidyReport.readiness.sbu', message: 'SBU confirmation' }));
    }
  }

  return (
    <div className="space-y-4" data-testid="subsidy-report-payload">
      <div className="grid grid-cols-2 gap-4">
        <FieldRow label={i18n._({ id: 'subsidyReport.detail.kind', message: 'Kind' })}>
          <Badge variant="secondary">{subsidyReportKindLabel(i18n, payload.kind)}</Badge>
        </FieldRow>
        <FieldRow label={i18n._({ id: 'subsidyReport.detail.dueOn', message: 'Due on' })}>
          <p className="text-sm">{formatDateDisplay(payload.due_on)}</p>
        </FieldRow>
        {payload.child_name ? (
          <FieldRow label={i18n._({ id: 'subsidyReport.detail.child', message: 'Child' })}>
            <p className="text-sm">{payload.child_name}</p>
          </FieldRow>
        ) : null}
        {payload.exit_date ? (
          <FieldRow label={i18n._({ id: 'subsidyReport.detail.exit', message: 'Exit date' })}>
            <p className="text-sm">
              {formatDateDisplay(payload.exit_date)}
              {payload.last_billed_month
                ? ` · ${i18n._({ id: 'subsidyReport.detail.lastBilledMonth', message: 'last billed month {month}', values: { month: payload.last_billed_month } })}`
                : ''}
            </p>
          </FieldRow>
        ) : null}
        {payload.filed_on ? (
          <FieldRow label={i18n._({ id: 'subsidyReport.detail.filedOn', message: 'Filed on' })}>
            <p className="text-sm">{formatDateDisplay(payload.filed_on)}</p>
          </FieldRow>
        ) : null}
        {payload.decision_on ? (
          <FieldRow label={i18n._({ id: 'subsidyReport.detail.decisionOn', message: 'Decision on' })}>
            <p className="text-sm" data-testid="subsidy-report-decision">
              {formatDateDisplay(payload.decision_on)}
              {payload.decision_number ? ` · ${payload.decision_number}` : ''}
            </p>
          </FieldRow>
        ) : null}
      </div>

      {payload.note ? (
        <FieldRow label={i18n._({ id: 'subsidyReport.detail.note', message: 'Note' })}>
          <p className="whitespace-pre-wrap text-sm">{payload.note}</p>
        </FieldRow>
      ) : null}

      {(payload.statement_rows ?? []).length > 0 ? (
        <FieldRow label={i18n._({ id: 'subsidyReport.detail.statement', message: 'Sozialdepartement differs' })}>
          <ul className="space-y-2 text-sm" data-testid="subsidy-report-statement-rows">
            {(payload.statement_rows ?? []).map((row) => (
              <li
                key={`${row.statement_id}-${row.child_month}`}
                className="rounded-md border p-2"
                data-testid={`subsidy-report-statement-row-${row.child_month}`}
              >
                <div className="font-medium">
                  {statementStateLabel(i18n, row.state)} · {row.child_month}
                </div>
                <div className="tabular-nums text-muted-foreground">
                  {i18n._({
                    id: 'subsidyReport.statement.figures',
                    message: 'City {city}, ours {expected}, difference {delta} (statement {period})',
                    values: {
                      city: formatMoneyCents(row.city_cents, 'CHF'),
                      expected: formatMoneyCents(row.expected_cents, 'CHF'),
                      delta: formatMoneyCents(row.delta_cents, 'CHF'),
                      period: row.statement_period,
                    },
                  })}
                </div>
                <Link
                  to={statementLink(row)}
                  className="text-primary underline-offset-2 hover:underline"
                  data-testid={`subsidy-report-statement-link-${row.child_month}`}
                >
                  {i18n._({ id: 'subsidyReport.statement.open', message: 'Open the statement' })}
                </Link>
              </li>
            ))}
          </ul>
        </FieldRow>
      ) : null}

      {payload.diff.length > 0 ? (
        <FieldRow label={i18n._({ id: 'subsidyReport.detail.diff', message: 'What changed' })}>
          <ul className="space-y-1 text-sm" data-testid="subsidy-report-diff">
            {payload.diff.map((change) => {
              const descriptor = SUBSIDY_REPORT_DIFF_LABELS[change.field];
              return (
                <li key={change.field}>
                  <span className="font-medium">{descriptor ? i18n._(descriptor) : change.field}:</span>{' '}
                  {change.before || '—'} → {change.after || '—'}
                </li>
              );
            })}
          </ul>
        </FieldRow>
      ) : null}

      {toFile ? (
        <Alert variant="warning" data-testid="subsidy-report-maximaltarif">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            {i18n._({
              id: 'subsidyReport.maximaltarif',
              message: 'Maximaltarif applies until approved; no retroactive subsidy.',
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">
            {i18n._({ id: 'subsidyReport.sheet.title', message: 'Values for Kollab' })}
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void copyText(buildSubsidyReportCopyAll(fields, labelOf), i18n)}
            data-testid="subsidy-report-copy-all"
          >
            <ClipboardCopy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            {i18n._({ id: 'subsidyReport.sheet.copyAll', message: 'Copy all' })}
          </Button>
        </div>
        <div className="rounded-md border px-3" data-testid="subsidy-report-sheet">
          {fields.map((field) => (
            <SheetRow key={field.key} field={field} label={labelOf(field)} i18n={i18n} />
          ))}
        </div>
      </div>

      {toFile ? (
        <div className="space-y-2">
          <Button
            type="button"
            className="w-full"
            disabled={!readiness.ready}
            onClick={() => setFileOpen(true)}
            data-testid="subsidy-report-mark-filed"
          >
            <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" />
            {i18n._({ id: 'subsidyReport.action.markFiled', message: 'Mark filed' })}
          </Button>
          {!readiness.ready ? (
            <p className="text-sm text-muted-foreground" data-testid="subsidy-report-not-ready">
              {i18n._({
                id: 'subsidyReport.readiness.missing',
                message: 'Attach a valid {documents} to the contract first.',
                values: { documents: missingDocs.join(' + ') },
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      {filed ? (
        <Button
          type="button"
          className="w-full"
          onClick={() => setDecisionOpen(true)}
          data-testid="subsidy-report-record-decision"
        >
          {i18n._({ id: 'subsidyReport.action.recordDecision', message: 'Record the decision' })}
        </Button>
      ) : null}

      {itemId ? (
        <>
          <SubsidyReportFileDialog open={fileOpen} onOpenChange={setFileOpen} itemId={itemId} />
          <SubsidyReportDecisionDialog open={decisionOpen} onOpenChange={setDecisionOpen} itemId={itemId} />
        </>
      ) : null}
    </div>
  );
}

export default SubsidyReportPayload;
